// Section 3: Ready Reckoner Rates
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { IndianRupee, TrendingUp } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";
import { REQ } from "../utils";

export const RRRatesSection = ({
  formData,
  handleChange,
  handleSelectChange,
  getInputClass,
  hasFieldError
}) => {
  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-rose-400" data-testid="quick-rr-rates">
      <CardHeader className="pb-3 bg-rose-100 rounded-t-xl border-b border-rose-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-rose-900">
          <IndianRupee className="h-5 w-5 text-muted-foreground" />
          3. Ready Reckoner Rates (₹ per Sq Mtr)
        </CardTitle>
        {formData.projectMode === "greenfield" && (
          <CardDescription className="text-sm text-muted-foreground">
            RR rates are required for TDR purchase calculations. Incentive FSI is not applicable for Greenfield projects.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* RR Rate - Plot */}
          <div className="space-y-2">
            <Label htmlFor="q-rrRatePlot">RR Rate - Plot *</Label>
            <IndianNumberInput
              id="q-rrRatePlot"
              name="rrRatePlot"
              value={formData.rrRatePlot}
              onChange={handleChange}
              placeholder="50,000"
              className={getInputClass("rrRatePlot", REQ)}
              data-testid="quick-rr-plot"
            />
            {hasFieldError("rrRatePlot") && <p className="text-xs text-red-500">Required</p>}
          </div>
          
          {/* RR Rate - Flat: Hidden for Commercial projects */}
          {formData.projectType !== "commercial" && (
            <div className="space-y-2">
              <Label htmlFor="q-rrRateFlat">RR Rate - Flat *</Label>
              <IndianNumberInput
                id="q-rrRateFlat"
                name="rrRateFlat"
                value={formData.rrRateFlat}
                onChange={handleChange}
                placeholder="30,000"
                className={getInputClass("rrRateFlat", REQ)}
                data-testid="quick-rr-flat"
              />
              {hasFieldError("rrRateFlat") && <p className="text-xs text-red-500">Required</p>}
            </div>
          )}
          
          {/* RR Rate - Office: Show for Commercial or Mixed Use */}
          {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
            <div className="space-y-2">
              <Label htmlFor="q-rrRateOffice">
                RR Rate - Office {formData.projectType === "commercial" ? "*" : ""}
              </Label>
              <IndianNumberInput
                id="q-rrRateOffice"
                name="rrRateOffice"
                value={formData.rrRateOffice}
                onChange={handleChange}
                placeholder="40,000"
                className={formData.projectType === "commercial" ? getInputClass("rrRateOffice", REQ) : "rounded-sm"}
                data-testid="quick-rr-office"
              />
              {formData.projectType === "commercial" && hasFieldError("rrRateOffice") && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
          )}
          
          {/* RR Rate - Shop: Show for Commercial or Mixed Use */}
          {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
            <div className="space-y-2">
              <Label htmlFor="q-rrRateShop">
                RR Rate - Shop {formData.projectType === "commercial" ? "*" : ""}
              </Label>
              <IndianNumberInput
                id="q-rrRateShop"
                name="rrRateShop"
                value={formData.rrRateShop}
                onChange={handleChange}
                placeholder="45,000"
                className={formData.projectType === "commercial" ? getInputClass("rrRateShop", REQ) : "rounded-sm"}
                data-testid="quick-rr-shop"
              />
              {formData.projectType === "commercial" && hasFieldError("rrRateShop") && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
          )}
        </div>
        
        {/* Additional RR Rate Markup - Not for MHADA */}
        {formData.projectClassification !== "mhada" && (
          <>
            <Separator />
            <div className="space-y-2">
              <Label className="font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Additional RR Rate Markup
              </Label>
              <p className="text-xs text-muted-foreground">
                Apply an additional % markup on RR Rate for TDR & FSI purchase cost calculations
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Apply Additional Markup?</Label>
                <Select
                  value={formData.applyRRMarkup || "no"}
                  onValueChange={(value) => handleSelectChange("applyRRMarkup", value)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="quick-rr-markup-toggle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.applyRRMarkup === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="q-rrAdditionalMarkupPercent">Markup Percentage (%)</Label>
                  <Input
                    id="q-rrAdditionalMarkupPercent"
                    name="rrAdditionalMarkupPercent"
                    type="number"
                    value={formData.rrAdditionalMarkupPercent || ""}
                    onChange={handleChange}
                    placeholder="10"
                    className="rounded-sm"
                    data-testid="quick-rr-markup-percent"
                  />
                </div>
              )}
              {formData.applyRRMarkup === "yes" && formData.rrAdditionalMarkupPercent > 0 && (
                <div className="space-y-2">
                  <Label>Effective RR Rate (Plot)</Label>
                  <p className="text-sm font-mono pt-1.5">
                    ₹{((parseFloat(formData.rrRatePlot) || 0) * (1 + (parseFloat(formData.rrAdditionalMarkupPercent) || 0) / 100)).toLocaleString('en-IN')}/sq mtr
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
